import { truncateDateToLocalDate, useErrorSnackbar } from '@lune-fe/lune-components-lib'
import { MainLayoutContainerNew, Pagination } from '@lune-fe/lune-ui-lib'
import Box from '@mui/material/Box'
import moment from 'moment'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Range } from 'react-date-range'

import { PredefinedRange } from 'components/DateRangeFilterNew'
import FiltersBoxNew from 'components/FiltersBoxNew'
import WhiteBoxContainer from 'components/WhiteBoxContainer'
import { getAllReports, PaginatedReports } from 'endpoints/dapi'
import useIsTestMode from 'hooks/useIsTestMode'
import useTitle from 'hooks/useTitle'
import { SnackbarMessages } from 'SnackbarMessages'
import ReportsTable from 'views/CalculateEmissions/Tables/ReportsTable/ReportsTable'

const Reports = () => {
    useTitle('Reports')
    const isTestMode = useIsTestMode()
    const ITEMS_PER_PAGE = 50
    const { enqueueSnackbar: snackbar } = useErrorSnackbar()

    const pageHistoryRef = useRef<(string | undefined)[]>([undefined])
    const [paginatedReports, setPaginatedReports] = useState<PaginatedReports>()

    const [loading, setLoading] = useState(true)
    const [currentPage, setCurrentPage] = useState(1)

    const [dateRange, setDateRange] = useState<Range | undefined>({
        startDate: moment().subtract(30, 'days').toDate(),
        endDate: moment().toDate(),
    })

    const hasCompletedInitialLoadRef = useRef(false)
    const postInitialRunCountRef = useRef(0)
    const previousFiltersRef = useRef<{
        dateRange: Range | undefined
    }>({
        dateRange: { startDate: undefined, endDate: undefined },
    })

    const loadReports = useCallback(
        async (
            {
                dateRange,
            }: {
                dateRange: Range | undefined
            },
            pageNumber: number,
        ) => {
            setLoading(true)
            const cursor = pageHistoryRef.current[pageNumber - 1]
            const result = await getAllReports({
                limit: ITEMS_PER_PAGE,
                after: cursor,
                from: dateRange?.startDate
                    ? truncateDateToLocalDate(dateRange.startDate)
                    : undefined,
                through: dateRange?.endDate
                    ? truncateDateToLocalDate(dateRange.endDate)
                    : undefined,
            })

            if (result.isOk()) {
                const paginatedResult = result.value
                setPaginatedReports(paginatedResult)
                if (paginatedResult.hasMore && paginatedResult.nextCursor) {
                    const nextCursor = paginatedResult.nextCursor
                    const newHistory = [...pageHistoryRef.current]
                    newHistory[pageNumber] = nextCursor
                    pageHistoryRef.current = newHistory
                }
            } else {
                setPaginatedReports({
                    hasMore: false,
                    nextCursor: null,
                    data: [],
                })
                snackbar(SnackbarMessages.GENERIC_ERROR, { error: result.error })
            }
            setLoading(false)
        },
        [snackbar],
    )

    const handlePageChange = (page: number) => {
        setCurrentPage(page)
        loadReports(
            {
                dateRange,
            },
            page,
        )
    }

    useEffect(
        // eslint-disable-next-line complexity
        () => {
            // Use dedicated ref to track if initial load is complete
            // This prevents filter updates during initialization from being treated as filter changes
            const isInitialLoad = !hasCompletedInitialLoadRef.current

            if (isInitialLoad) {
                // Save the initial filter state
                previousFiltersRef.current = {
                    dateRange,
                }
                // Mark initial load as complete immediately to prevent subsequent runs from loading again
                hasCompletedInitialLoadRef.current = true
                // Load data with restored page
                loadReports(
                    {
                        dateRange,
                    },
                    1,
                )
            } else {
                // POST-INITIAL LOAD: Only reload if filters actually changed
                // Increment post-initial run counter
                postInitialRunCountRef.current += 1

                const filtersChanged =
                    previousFiltersRef.current.dateRange?.startDate !== dateRange?.startDate ||
                    previousFiltersRef.current.dateRange?.endDate !== dateRange?.endDate

                if (filtersChanged) {
                    // Real filter change after settling: reset pagination to page 1
                    setCurrentPage(1)
                    previousFiltersRef.current = {
                        dateRange,
                    }
                    // Load data from page 1
                    loadReports(
                        {
                            dateRange,
                        },
                        1,
                    )
                }
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },
        [dateRange],
    )

    return (
        <MainLayoutContainerNew
            isTestMode={!!isTestMode}
            title={'Reports'}
            subtitle={'Generate, track, and access all your reports.'}
            sx={{
                height: isTestMode ? `calc(100vh - 24px)` : '100vh',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                '.main-layout-container__header': {
                    top: 0,
                },
                '.main-layout-container__body': {
                    overflowY: 'hidden',
                },
            }}
        >
            <WhiteBoxContainer
                sx={{
                    overflow: 'hidden',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Box
                    sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <FiltersBoxNew
                        filters={[]}
                        preselectedDateRange={dateRange}
                        setDateRange={setDateRange}
                        defaultDateRange={PredefinedRange.THIRTY_DAYS}
                    />
                    {paginatedReports && paginatedReports.data.length > 0 && (
                        <Pagination
                            currentPage={currentPage}
                            itemsPerPage={ITEMS_PER_PAGE}
                            currentPageItemCount={paginatedReports.data.length}
                            onPageChange={handlePageChange}
                            defaultHasNext={paginatedReports.hasMore}
                            loading={loading}
                        />
                    )}
                </Box>
                <ReportsTable loading={loading} paginatedReports={paginatedReports} />
            </WhiteBoxContainer>
        </MainLayoutContainerNew>
    )
}

export default Reports
